<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  type: {
    type: String,
    default: '10m'
  }
})
const headers = ['時刻', '水位', '水位変化', '水位警戒基準']
const table = ref([])
const waterLevelData = computed(() => {
  const arr = props.data[`observation_${props.type}`]
  return [...arr].sort((a, b) => b.tm - a.tm)
})
const levelList = computed(() => {
  const list = [
    ...(props.data.warn_water_level ? [{ rank: 2, value: props.data.warn_water_level }] : []),
    ...(props.data.alarm_water_level ? [{ rank: 3, value: props.data.alarm_water_level }] : []),
    ...(props.data.danger_water_level ? [{ rank: 4, value: props.data.danger_water_level }] : []),
    ...(props.data.high_water_level ? [{ rank: 5, value: props.data.high_water_level }] : [])
  ]
  return list
})

const setDiff = (index) => {
  const next = waterLevelData.value[index]?.water_level
  const pre = waterLevelData.value[index + 1]?.water_level
  if (pre == null && next) return 999
  if (index === waterLevelData.value.length - 1) return 999
  const diffValue = next - pre
  if (diffValue > 0) {
    return 1
  } else if (diffValue < 0) {
    return -1
  }
  return 0
}
const makeTable = () => {
  const result = []
  let tmpDate = null
  let tmpDateData = null
  waterLevelData.value.forEach((item, index) => {
    const date = dayjs.unix(item.tm).tz('Asia/Tokyo').format('D日(ddd)')
    const time = dayjs.unix(item.tm).tz('Asia/Tokyo').format('H:mm')
    if (date !== tmpDate) {
      if (tmpDateData) {
        result.push(tmpDateData)
      }
      tmpDate = date
      tmpDateData = {
        date: tmpDate,
        contents: []
      }
    }
    const content = {
      time: time,
      level: item.water_level,
      diff: setDiff(index),
      rank: setRank(item.water_level, levelList.value)
    }
    tmpDateData.contents.push(content)
  })
  if (tmpDateData) {
    result.push(tmpDateData)
  }
  return result
}

watch(() => props.type, () => {
  table.value = makeTable()
}, { immediate: true })
</script>

<template>
  <div class="table">
    <div class="table__head">
      <div
        v-for="(head, headIndex) in headers"
        :key="headIndex"
        class="table__item"
      >
        {{ head }}
      </div>
    </div>
    <div class="table__body">
      <template
        v-for="(item, tableIndex) in table"
        :key="tableIndex"
      >
        <p class="table__date">
          {{ item.date }}
        </p>
        <div
          v-for="(row, contentsIndex) in item.contents"
          :key="contentsIndex"
          class="table__row"
        >
          <div class="table__item">
            {{ row.time }}
          </div>
          <div class="table__item">
            {{ row.level ? `${row.level.toFixed(2)}m` : '-' }}
          </div>
          <div class="table__item">
            <p v-if="row.diff !== 999 && !row.level">
              -
            </p>
            <svg
              v-if="row.diff !== 999 && row.level"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="12"
              viewBox="0 0 15 12"
              :data-rank="row.rank"
              :style="`transform: rotate(calc(1deg * ${row.diff*(-45)}))`"
            >
              <path
                d="M14.4925 6.55748C14.7646 6.28546 14.7646 5.84443 14.4925 5.57241L10.0597 1.13959C9.7877 0.867575 9.34667 0.867575 9.07465 1.13959C8.80263 1.41161 8.80263 1.85264 9.07465 2.12466L13.0149 6.06494L9.07465 10.0052C8.80263 10.2772 8.80263 10.7183 9.07465 10.9903C9.34667 11.2623 9.7877 11.2623 10.0597 10.9903L14.4925 6.55748ZM0 6.06494V6.76149H14V6.06494V5.36839H0V6.06494Z"
                fill="#3569C0"
              />
            </svg>
          </div>
          <div class="table__item">
            {{ setRankText[row.rank] }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table {
  display: flex;
  flex-direction: column;
  gap: 2px;
  &__head,
  &__row {
    display: flex;
    gap: 1px;
  }
  &__head {
    background-color: #FFF;
  }
  &__row {
    z-index: 1;
    border-bottom: 1px solid #CCC;
  }
  &__item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-block: 10px;
    text-align: center;
    color: #303030;
    font-size: 14px;
    line-height: 1em;
    &:is(.table__head *) {
      background-color: #EDEAD4;
      font-weight: 600;
    }
    &:is(.table__row *) {
      font-weight: 300;
      letter-spacing: 0.25px;
    }
    svg{
      &[data-rank='0'] path {
        fill: #148dff;
      }
      &[data-rank='1'] path {
        fill: #6fb28a;
      }
      &[data-rank='2'] path {
        fill: #e7dc00;
      }
      &[data-rank='3'] path {
        fill: #ff2800;
      }
      &[data-rank='4'] path {
        fill: #aa00aa;
      }
      &[data-rank='5'] path {
        fill: #0c000ccc;
      }
    }
  }
  &__body {
    display: flex;
    flex-direction: column;
    max-height: 463px;
    overflow-y: auto;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 100vmax;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background: #438fec;
    }
  }
  &__date {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #F6F6F1;
    padding-block: 10px;
    color: #303030;
    font-size: 14px;
    font-weight: 600;
    line-height: 1em;
    text-align: center;
  }
}
@media (any-hover: hover) {
}
@media (min-width: 830px) {
  .table {
    &__head {
      top: 128px;
    }
    &__body {
      max-height: 481px;
    }
    &__item {
      font-size: 16px;
    }
    &__date {
      font-size: 16px;
    }
    &__row {
      border-right: 1px solid #e0e0cc;
      border-left: 1px solid #e0e0cc;
    }
  }
}
</style>
